import { CapacitorHttp } from "@capacitor/core";

import { isNativeApp } from "./runtime";

const HTTP_URL_RE = /^https?:\/\//i;
const DATA_IMAGE_URL_RE = /^data:image\/[a-z0-9.+-]+;base64,/i;
const IMAGE_MIME_BY_EXTENSION = {
  avif: "image/avif",
  gif: "image/gif",
  jpeg: "image/jpeg",
  jpg: "image/jpeg",
  png: "image/png",
  svg: "image/svg+xml",
  webp: "image/webp",
};

const findHeader = (headers, name) => {
  const normalizedName = String(name || "").toLowerCase();
  const entry = Object.entries(headers || {}).find(
    ([key]) => String(key).toLowerCase() === normalizedName
  );
  return String(entry?.[1] || "").trim();
};

const inferImageMimeType = (url) => {
  try {
    const pathname = new URL(url).pathname;
    const extension = pathname.split(".").pop()?.toLowerCase();
    return IMAGE_MIME_BY_EXTENSION[extension] || "";
  } catch {
    return "";
  }
};

export const shouldFetchImageNatively = (src) =>
  isNativeApp() && HTTP_URL_RE.test(String(src || "").trim());

export async function fetchNativeImageAsDataUrl(src) {
  const url = String(src || "").trim();
  if (!HTTP_URL_RE.test(url)) {
    throw new Error("Native image request requires an absolute HTTP(S) URL");
  }

  const response = await CapacitorHttp.get({
    url,
    responseType: "blob",
    headers: { Accept: "image/*" },
    connectTimeout: 15000,
    readTimeout: 30000,
  });

  if (response.status < 200 || response.status >= 300) {
    throw new Error(`Failed to fetch image: ${response.status}`);
  }

  const rawData = String(response.data || "").trim();
  if (!rawData) {
    throw new Error("Native image response was empty");
  }
  if (DATA_IMAGE_URL_RE.test(rawData)) return rawData;

  const responseMimeType = findHeader(response.headers, "content-type")
    .split(";", 1)[0]
    .trim()
    .toLowerCase();
  const mimeType = responseMimeType.startsWith("image/")
    ? responseMimeType
    : inferImageMimeType(response.url || url);

  if (!mimeType) {
    throw new Error("Native image response did not contain an image");
  }

  return `data:${mimeType};base64,${rawData}`;
}

export async function resolveImageSourceForPixelRead(src) {
  const url = String(src || "").trim();
  if (!url || !shouldFetchImageNatively(url)) return url;
  return fetchNativeImageAsDataUrl(url);
}
